import React, { useEffect } from 'react';

import RealtimeContext from './RealtimeContext';
import AppSyncConfig from '../../app-sync-config.json';
import { useApolloClient } from '@apollo/client';
import { subscribeToProjectChanges, getProject } from 'graph/queries';
import { getAction } from 'utils/cache';
import { realtimeStrategyFor } from './realtimeStrategy';
import { applyRealtimeChange } from './realtimeCache';

let recentUpdates = {};
let recentCreations = {};

// Réconciliation ciblée des changements IMBRIQUÉS (scenarios, impacts, liens…)
// que le fast-path scalaire ne peut pas merger. Plutôt qu'un getProject complet,
// on ne re-fetch QUE la section de l'objet concerné, via les flags include* déjà
// présents sur getProject. Débouncé + coalescé par type.
const INCLUDE_BY_TYPE: Record<string, any> = {
	Stakeholder: { includeStakeholders: true, includeRisks: false, includeIndicators: false, includeFishbone: false },
	Risk: { includeStakeholders: false, includeRisks: true, includeIndicators: false, includeFishbone: false },
	Indicator: { includeStakeholders: false, includeRisks: false, includeIndicators: true, includeFishbone: false },
	FishboneBranch: { includeStakeholders: false, includeRisks: false, includeIndicators: false, includeFishbone: true },
	FishboneItem: { includeStakeholders: false, includeRisks: false, includeIndicators: false, includeFishbone: true },
	// Stake/Scenario sont toujours renvoyés par getProject (pas de flag) : on
	// coupe tout le reste pour la charge minimale.
	Stake: { includeStakeholders: false, includeRisks: false, includeIndicators: false, includeFishbone: false },
	Scenario: { includeStakeholders: false, includeRisks: false, includeIndicators: false, includeFishbone: false }
};
let reconcileTimer: ReturnType<typeof setTimeout> | null = null;
let pendingReconcileTypes = new Set<string>();
let pendingReconcileProjectId: string | null = null;

// @ts-ignore
let subdomain: string = document.location.hostname.replace(/\.gerositus.*/, '');
const config =
	import.meta.env.VITE_APP_MODE === 'corporate'
		? AppSyncConfig[import.meta.env.VITE_APP_MODE][import.meta.env.VITE_APP_CLIENT || subdomain][
		import.meta.env.VITE_APP_ENV
		]
		: AppSyncConfig[import.meta.env.VITE_APP_MODE][
		import.meta.env.VITE_APP_ENV
		];


export const RealtimeProvider = ({ children }) => {

	const apolloClient = useApolloClient();

	// Using Graphql subscription
	const subscribeToProject = async (projectId: string) => {
		const subscription = apolloClient.subscribe({
			query: subscribeToProjectChanges,
			variables: {
				projectId
			}
		}).subscribe({
			next: (data) => {
				if (!data?.data?.onProjectChange?.changes) return null;

				const changes = JSON.parse(data.data.onProjectChange.changes);
				const defaultDetails = {
					description: "",
					scenarios: [],
					comments: [],
					links: [],
					taggings: [],
					icon: null,
					trend: null,
					impact: "low",
					impacts: [],
				}
				const details = {
					...defaultDetails,
					projectId: data.data.onProjectChange.projectId,
					id: data.data.onProjectChange.objectId,
					__typename: data.data.onProjectChange.type,
					...changes
				}
				const type = data.data.onProjectChange.type
				const changeFunctions = {}

				// Fast-path : merge direct des champs SCALAIRES dans le cache
				// normalisé (feedback immédiat). Les champs IMBRIQUÉS (scenarios,
				// impacts, liens…) ne peuvent pas être mergés de façon fiable ici
				// (formes normalisées côté client) — on les traite par un refetch
				// de sécurité plus bas. `...changes` inclut les champs inconnus,
				// donc un NOUVEAU champ scalaire est géré sans rien changer ici.
				let hasNestedChange = false;
				for (const key in changes) {
					const value = changes[key];
					if (value !== null && typeof value === 'object') {
						// Champ imbriqué (existant ou futur) OU dérivé d'une table
						// enfant -> refetch de réconciliation (voir plus bas).
						hasNestedChange = true;
						continue;
					}
					if (details.hasOwnProperty(key)) {
						changeFunctions[key] = () => details[key];
					}
				}

				// Réconciliation CIBLÉE d'un changement imbriqué : on ne re-fetch que
				// la section de l'objet concerné (flags include* de getProject), pas
				// tout l'arbre. Coalescé par type ; si plusieurs types changent en
				// rafale (ou type inconnu), on retombe sur un getProject complet.
				const scheduleReconcile = (changedType: string, projectId: string) => {
					pendingReconcileTypes.add(changedType);
					pendingReconcileProjectId = projectId;
					if (reconcileTimer) clearTimeout(reconcileTimer);
					reconcileTimer = setTimeout(() => {
						reconcileTimer = null;
						const types = [...pendingReconcileTypes];
						const pid = pendingReconcileProjectId;
						pendingReconcileTypes = new Set();
						pendingReconcileProjectId = null;

						const single = types.length === 1 ? INCLUDE_BY_TYPE[types[0]] : null;
						if (single && pid) {
							// Section unique -> fetch ciblé (réécrit ces objets dans le
							// cache normalisé ; scenarios normalisés comme au chargement).
							apolloClient.query({
								query: getProject,
								variables: { id: pid, ...single },
								fetchPolicy: 'network-only'
							}).catch(() => { });
						} else {
							// Plusieurs sections ou type non mappé -> filet complet.
							apolloClient.refetchQueries({ include: ['GetProject'] }).catch(() => { });
						}
					}, 400);
				};

				// Fishbone (branches sous Project.fishboneBranchs, items sous
				// FishboneBranch.items) : pas de champ Project générique -> la
				// stratégie renvoie 'reconcile' (refetch ciblé includeFishbone).
				const strategy = realtimeStrategyFor(type, data.data?.onProjectChange?.operation);

				applyRealtimeChange(apolloClient.cache, {
					strategy,
					type,
					projectId: data.data?.onProjectChange?.projectId,
					details,
					changes,
					changeFunctions
				});

				if (data.data?.onProjectChange?.operation == "INSERT") {
					recentCreations[data.data.onProjectChange.objectId] = true;
					setTimeout(() => {
						delete recentCreations[data.data.onProjectChange.objectId];
					}, 6000)
				}

				// Les champs scalaires sont déjà mergés (fast-path ci-dessus, y
				// compris les nouveaux). Seuls les champs IMBRIQUÉS (scenarios,
				// impacts, liens…) déclenchent une réconciliation ciblée de leur
				// section -> on ne loupe rien, sans getProject complet ni merge par
				// champ à maintenir.
				if (strategy === 'reconcile' || hasNestedChange) {
					scheduleReconcile(type, data.data.onProjectChange.projectId);
				}
			},
			error: (error) => {
				console.error('subscription error', error);
			}
		});
	}

	const subscribeToProjects = async (projectId: string) => {
		// No-op : onCreateProject a été retiré du schéma (diffusait chaque projet créé à tous les utilisateurs).
	}

	const state = {
		subscribeToProjects,
		subscribeToProject,
		recentCreations,
		recentUpdates,
		unsubscribeFromProject: () => { },
		unsubscribeFromProjects: () => { },
		executeAction: (projectId, actionName, details) => {
			const fiteredActionName = actionName.replace(/(create|update|delete|clear|reset)/, '');
			const actionType = actionName.replace(fiteredActionName, '');
			delete details.delay;

			if (actionType == "create") {
				recentCreations[details.id] = true;
				setTimeout(() => {
					delete recentCreations[details.id];
				}, 6000)
			}

			const action = getAction(actionName);
			if (action) {
				action(apolloClient, projectId, fiteredActionName, details);
			}
		}
	};

	return (
		<RealtimeContext.Provider value={state}>
			{children}
		</RealtimeContext.Provider>
	);
}